import { StringList } from '@trustero/trustero-api-web/lib/common/collections_pb'
import {
  RESPONSIBILITY,
  ResponsibilityValue,
} from '@trustero/trustero-api-web/lib/common/model_pb'
import {
  CONTROL_STATUS,
  ControlStatusValue,
  CreateUpdateControlRequest,
} from '@trustero/trustero-api-web/lib/model/control_pb'
import { Department } from '@trustero/trustero-api-web/lib/model/department_pb'
import { PolicyRecord } from '@trustero/trustero-api-web/lib/model/policy_pb'
import { UserRecord } from '@trustero/trustero-api-web/lib/account/account_pb'
import { Risk } from '@trustero/trustero-api-web/lib/risk/risk_pb'
import { VendorRecord } from '@trustero/trustero-api-web/lib/vendormanagement/vendormanagement_pb'
import { StringValue } from 'google-protobuf/google/protobuf/wrappers_pb'
import {
  Responsibility,
  ResponsibilityOptions,
  ResponsibilityToOptions,
} from 'src/components/PageLayout/ShowPage/Dropdowns/Dropdowns.constants'
import {
  controlStatusTitle,
  controlStatusToTitle,
} from 'src/lib/common/types/control'
import {
  createLowercaseLookupMap,
  CSV_COLUMN_VALIDATIONS,
  CSV_VALIDATIONS,
  HeaderValues,
  ParseColumnType,
  RowValidator,
} from 'src/Utils/csv/csv.constants'
import {
  NONE_ID,
  NONE_NAME,
  NO_REQUIREMENTS_INDICATOR,
} from 'src/Utils/globalConstants'
import { Owner } from 'src/Utils/globalEnums'
import { separateValues, stripOuterCsvQuotes } from 'src/Utils/csv/csv.helpers'
import { ControlStatusItem } from '@trustero/trustero-api-web/lib/model/control_status_pb'
import { ComplianceFrameworkGroup } from 'src/Utils/globalTypes'

export const CONTROL_CSV_TEMPLATE: { [key: string]: string }[] = [
  {
    'Control ID': 'CTRL-0001',
    'Control Name': 'This is a sample control',
    Objective: 'This is a sample objective',
    Notes: 'This is a sample note',
    Procedure: 'This is a sample procedure',
    Policy: '',
    Department: '',
    Status: 'Needs Attention',
    Responsibility: 'Direct responsibility',
    Reason: 'None',
    Vendor: 'None',
    Owner: 'Unassigned',
    Assignees: 'Unassigned',
    'Required Evidence': 'sample required evidence',
    'Test Procedures': 'sample test procedures',
  },
  {
    'Control ID': 'CTRL-0002',
    'Control Name': 'This is a sample control',
    Objective: 'This is a sample objective',
    Notes: 'This is a sample note',
    Procedure: 'This is a sample procedure',
    Policy: '',
    Department: '',
    Status: 'Needs Attention',
    Responsibility: 'Direct responsibility',
    Reason: 'None',
    Vendor: 'None',
    Owner: 'Unassigned',
    Assignees: 'Unassigned',
    'Required Evidence': 'sample required evidence',
    'Test Procedures': 'sample test procedures',
  },
]

export interface ImportControlCSVRow {
  [key: string]: string
}

const splitPipeValues = (value: string, preserveEmpty: boolean): string[] => {
  if (!value?.trim()) return []
  const parts = value.split('|').map((part) => part.trim())
  return preserveEmpty ? parts : parts.filter((part) => part !== '')
}

const normalizeFrameworkRequirement = (value: string): string => {
  return value
    .trim()
    .toLowerCase()
    .replace(/\s*,\s*/g, ', ')
    .replace(/\s+/g, ' ')
}

const parseFrameworkRequirements = (
  value: string,
  validRequirementNames: string[],
): {
  names: string[]
  invalidFragments: string[]
} => {
  const normalizedToCanonical = new Map<string, string>()
  validRequirementNames.forEach((name) => {
    normalizedToCanonical.set(normalizeFrameworkRequirement(name), name)
  })

  // Do not strip outer quotes before tokenizing: a cell like
  // "11.1 Event Monitoring","8.4 Capacity Management" must stay quoted so
  // separateValues can split on commas between fields.
  const raw = value.trim()
  if (!raw) {
    return { names: [], invalidFragments: [] }
  }

  const tryWhole = (s: string) =>
    normalizedToCanonical.get(normalizeFrameworkRequirement(s))

  // Single requirement whose title contains commas (often one outer-quoted field).
  let whole = tryWhole(raw)
  if (!whole) {
    const unwrapped = stripOuterCsvQuotes(raw)
    if (unwrapped !== raw) {
      whole = tryWhole(unwrapped)
    }
  }
  if (whole) {
    return { names: [whole], invalidFragments: [] }
  }

  const tokens = separateValues(raw)
    .map((t) => stripOuterCsvQuotes(t).trim())
    .filter((t) => t !== '')
  if (tokens.length === 0) {
    return { names: [], invalidFragments: [] }
  }

  const names: string[] = []
  const invalidFragments: string[] = []

  for (let i = 0; i < tokens.length; ) {
    let matchedName = ''
    let nextIndex = i + 1

    // Find the longest match so values containing commas are reconstructed.
    for (let j = tokens.length; j > i; j--) {
      const combined = tokens.slice(i, j).join(', ')
      const canonical = normalizedToCanonical.get(
        normalizeFrameworkRequirement(combined),
      )
      if (canonical) {
        matchedName = canonical
        nextIndex = j
        break
      }
    }

    if (matchedName) {
      names.push(matchedName)
      i = nextIndex
      continue
    }

    invalidFragments.push(tokens[i])
    i++
  }

  return { names, invalidFragments }
}

/** After CSV parse + mutator, multiple requirements are ';'-separated (never comma-split here). */
const splitFrameworkCellForUpload = (cell: string): string[] => {
  if (!cell?.trim()) return []
  return cell
    .split(';')
    .map((part) => stripOuterCsvQuotes(part).trim())
    .filter((part) => part !== '')
}

const getPolicySlots = (value: string): string[] => {
  if (!value?.trim()) return []
  return value.includes('|')
    ? value.split('|').map((part) => part.trim())
    : [value.trim()]
}

// Parses a numeric enum code (status/responsibility values are mutated to their
// enum codes before row validation). Returns -1 for blank/non-numeric input —
// note 0 is a valid code, so a plain `Number(x) || -1` would be wrong.
const parseEnumIndex = (value?: string): number => {
  const trimmed = value?.trim() ?? ''
  if (trimmed === '') return -1
  const parsed = Number(trimmed)
  return Number.isNaN(parsed) ? -1 : parsed
}

const resolvePolicyModelIds = (
  policyValue: string,
  policies: PolicyRecord[],
): string[] => {
  const slots = splitPipeValues(policyValue, false)
  if (slots.length === 0) return []

  return slots
    .map((slot) => {
      const normalized = slot.toLowerCase()
      const matchedPolicy = policies.find(
        (policy) => policy.getName().trim().toLowerCase() === normalized,
      )
      if (matchedPolicy) return matchedPolicy.getModelId()
      const matchedByModelId = policies.find(
        (policy) => policy.getModelId().toLowerCase() === normalized,
      )
      return matchedByModelId?.getModelId() || ''
    })
    .filter(Boolean)
}

export const CONTROL_CSV_HEADERS: HeaderValues[] = [
  {
    input: 'Control ID',
    output: 'modelId',
  },
  {
    input: 'Control Name',
    output: 'name',
  },
  {
    input: 'Objective',
    output: 'objective',
  },
  {
    input: 'Notes',
    output: 'notes',
    optional: true,
  },
  {
    input: 'Procedure',
    output: 'procedure',
    optional: true,
  },
  {
    input: 'Policy',
    output: 'policy',
    optional: true,
  },
  {
    input: 'Department',
    output: 'department',
    optional: true,
  },
  {
    input: 'Status',
    output: 'status',
    optional: true,
  },
  {
    input: 'Responsibility',
    output: 'responsibility',
    optional: true,
  },
  {
    input: 'Reason',
    output: 'reason',
    optional: true,
  },
  {
    input: 'Vendor',
    output: 'vendor',
    optional: true,
  },
  {
    input: 'Owner',
    output: 'owner',
    optional: true,
  },
  {
    input: 'Assignees',
    output: 'assignees',
    optional: true,
  },
  {
    input: 'Required Evidence',
    output: 'requiredEvidence',
    optional: true,
  },
  {
    input: 'Test Procedures',
    output: 'testProcedures',
    optional: true,
  },
  {
    input: 'Risks',
    output: 'risks',
    optional: true,
  },
]

export const getControlParsers = (
  frameworks: { [key: string]: ComplianceFrameworkGroup },
  policies: PolicyRecord[],
  vendors: VendorRecord[] = [],
  departments: Department[] = [],
  risks: Risk[] = [],
  statuses: ControlStatusItem[] = [],
  users: UserRecord[] = [],
): ParseColumnType => {
  const userEmailSet = new Set(users.map((user) => user.getEmail()))
  const validators: ParseColumnType = {
    modelId: {
      // Imports may now both add new controls and modify existing ones, so a
      // model_id that already exists in the account is allowed (it updates that
      // control); only the length is validated.
      validator: (columnName, value, row) =>
        CSV_VALIDATIONS.LENGTH(columnName, value, 1, 255, row),
      mutator: (value: string) => value.slice(0, 255),
    },
    name: {
      validator: (columnName, value, row) =>
        CSV_VALIDATIONS.LENGTH(columnName, value, 1, 255, row),
      mutator: (value: string) => value.slice(0, 255),
    },
    objective: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    policy: {
      validator: (_c, value, row) => {
        // Split preserving empties so blank slots ("A||B") can be rejected; the
        // mutator strips empties, so this is the only place they're visible.
        const rawSlots = value.includes('|')
          ? value.split('|').map((slot) => slot.trim())
          : value.trim()
          ? [value.trim()]
          : []
        if (rawSlots.length === 0) return null
        if (rawSlots.some((slot) => slot === '')) {
          return `Row ${row}: Policy entries cannot be empty when using "|".`
        }
        // "none" means the control has no policy, so it can't be combined with
        // real policies.
        const hasNone = rawSlots.some((slot) => slot.toLowerCase() === NONE_ID)
        if (hasNone && rawSlots.length > 1) {
          return `Row ${row}: "${NONE_NAME}" cannot be combined with other policies.`
        }
        const lowerPolicyNames = policies.map((policy) =>
          policy.getName().trim().toLowerCase(),
        )
        const lowerPolicyModelIds = policies.map((policy) =>
          policy.getModelId().toLowerCase(),
        )
        const invalidSlot = rawSlots.find((slot) => {
          const normalized = slot.toLowerCase()
          return (
            normalized !== NONE_ID &&
            !lowerPolicyNames.includes(normalized) &&
            !lowerPolicyModelIds.includes(normalized)
          )
        })
        if (!invalidSlot) return null
        return `Row ${row}: "${invalidSlot}" is not a valid policy.`
      },
      mutator: (value: string) => {
        if (value.trim().toLowerCase() === NONE_ID) return ''
        return splitPipeValues(value, false).join('|').slice(0, 255)
      },
    },
    procedure: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    notes: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    requiredEvidence: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    testProcedures: {
      validator: (_c, _v, _r) => null,
      mutator: (value) => value,
    },
    status: {
      validator: (_c, value, row) =>
        CSV_VALIDATIONS.VALUE_IN_ARRAY(
          value,
          row,
          statuses.map((status) => status.getName()),
        ),
      mutator: (value) => {
        if (!value) return value

        const lowerCaseLookup = createLowercaseLookupMap(controlStatusToTitle)
        const adjustedValue = value.trim().toLowerCase()
        return adjustedValue in lowerCaseLookup
          ? `${lowerCaseLookup[adjustedValue]}`
          : value
      },
    },
    responsibility: {
      // Validate against the values the mutator can actually convert. This
      // excludes TMP_OUTSOURCED ("Outsourced"), which isn't a valid control
      // responsibility and has no entry in ResponsibilityToOptions.
      validator: (_c, value, row) =>
        CSV_VALIDATIONS.VALUE_IN_ARRAY(
          value,
          row,
          Object.keys(ResponsibilityToOptions),
          Object.keys(ResponsibilityToOptions),
        ),
      mutator: (value) => {
        if (!value) return value

        const lowerCaseLookup = createLowercaseLookupMap(
          ResponsibilityToOptions,
        )
        const adjustedValue = value.trim().toLowerCase()
        return adjustedValue in lowerCaseLookup
          ? `${lowerCaseLookup[adjustedValue]}`
          : value
      },
    },
    reason: {
      validator: (_c, _v, _r) => null, // anything goes
      mutator: (value) => value,
    },
    vendor: {
      validator: (_c, value, row) => {
        const vendorNames = [
          NONE_NAME,
          ...vendors.map((vendor) => vendor.getName()),
        ]
        return CSV_VALIDATIONS.VALUE_IN_ARRAY(
          value.trim().toLowerCase(),
          row,
          vendorNames,
          vendorNames,
        )
      },
      mutator: (value) => {
        if (!value) return value
        if (value.trim().toLowerCase() === NONE_NAME.toLowerCase()) {
          return ''
        }

        const updatedValue = value.trim().toLowerCase()

        // Resolve to the canonical vendor name (not id): the staging RPC carries
        // the vendor by name (toStagedControl -> StagedControl.vendorName) and the
        // backend resolves the name to a vendor row. Returning an id here would
        // both display the raw id in the diff and break that name resolution.
        return (
          vendors
            .find((vendor) => vendor.getName().toLowerCase() === updatedValue)
            ?.getName() || ''
        )
      },
    },
    owner: {
      validator: (_c, value, row) => {
        const trimmed = value.trim()
        // Skip when "Unassigned" or when the account users haven't loaded yet
        // (an empty set would otherwise reject every email).
        if (
          !trimmed ||
          trimmed.toLowerCase() === Owner.UNASSIGNED_BE ||
          userEmailSet.size === 0
        ) {
          return null
        }
        return CSV_VALIDATIONS.USER_EMAIL(trimmed, userEmailSet, row)
      },
      mutator: (value) => value.trim(),
    },
    assignees: {
      validator: (_c, value, row) => {
        const trimmed = value?.trim()
        if (
          !trimmed ||
          trimmed.toLowerCase() === Owner.UNASSIGNED_BE ||
          userEmailSet.size === 0
        )
          return null
        const emails = trimmed
          .split(',')
          .map((e) => e.trim())
          .filter(Boolean)
        for (const email of emails) {
          const err = CSV_VALIDATIONS.USER_EMAIL(email, userEmailSet, row)
          if (err) return err
        }
        return null
      },
      mutator: (value) => {
        const trimmed = value.trim()
        if (trimmed.toLowerCase() === Owner.UNASSIGNED_BE) return ''
        return trimmed
          .split(',')
          .map((e) => e.trim())
          .filter(Boolean)
          .join(',')
      },
    },
    department: {
      validator: (_c, value, row) => {
        const departmentNames = [
          NONE_NAME,
          ...departments.map((department) => department.getName()),
          ...departments.map((department) => department.getShortName()),
        ]
        const slots = splitPipeValues(value, true)
        const invalidSlot = slots.find((slot) => {
          if (!slot) return false
          return !departmentNames.some(
            (candidate) => candidate.toLowerCase() === slot.toLowerCase(),
          )
        })
        if (!invalidSlot) return null
        return `Row ${row}: "${invalidSlot}" is not a valid department.`
      },
      mutator: (value) => splitPipeValues(value, true).join('|'),
    },
    risks: {
      validator: (_c, value, row) => {
        if (!value?.trim()) return null
        const riskCustomIds = risks.map((r) => r.getCustomId().toLowerCase())
        const entries = separateValues(value)
        for (const entry of entries) {
          const customId = entry.split(/\s+/)[0]?.trim()
          if (!customId) continue
          if (!riskCustomIds.includes(customId.toLowerCase())) {
            return `Row ${row}: Risk "${customId}" not found.`
          }
        }
        return null
      },
      mutator: (value) => {
        if (!value?.trim()) return ''
        const entries = separateValues(value)
        return entries
          .map((entry) => entry.split(/\s+/)[0]?.trim())
          .filter(Boolean)
          .join(',')
      },
    },
  }

  Object.entries(frameworks).forEach(([name, objectives]) => {
    const names: string[] = objectives.objectives.map((objective) => {
      return objective.getName()
    })
    validators[name] = {
      validator: (_c, value, row) => {
        // "<no requirements>" means the control is linked to the framework with
        // no requirements — a valid cell, not a requirement to validate.
        if (value.trim() === NO_REQUIREMENTS_INDICATOR) return null
        const { invalidFragments } = parseFrameworkRequirements(value, names)
        if (invalidFragments.length === 0) return null
        return `Row ${row}: '${invalidFragments[0]}' not one of the expected values. Double check the item is correct.`
      },
      mutator: (value) => {
        if (value.trim() === NO_REQUIREMENTS_INDICATOR) {
          return NO_REQUIREMENTS_INDICATOR
        }
        const parsed = parseFrameworkRequirements(value, names)
        return parsed.names.join(';')
      },
    }
  })

  return validators
}

export const getControlRowValidators = (
  policies: PolicyRecord[],
  departments: Department[],
): RowValidator[] => {
  // Resolve a policy from a Policy-column slot (a name or a model id), and a
  // department from its id, for the policy/department cross-check below.
  const policyByKey = new Map<string, PolicyRecord>()
  policies.forEach((policy) => {
    policyByKey.set(policy.getName().trim().toLowerCase(), policy)
    policyByKey.set(policy.getModelId().toLowerCase(), policy)
  })
  const departmentById = new Map<string, Department>()
  departments.forEach((department) => {
    departmentById.set(department.getId(), department)
  })

  const validateResponsibility: RowValidator = (
    row: Record<string, string>,
    rowNumber: number,
  ): string | null => {
    const responsibilityIndex = parseEnumIndex(row.responsibility)
    const responsibility =
      ResponsibilityOptions[responsibilityIndex as RESPONSIBILITY] || ''
    const statusIndex = parseEnumIndex(row.status)
    const status = controlStatusTitle[statusIndex as CONTROL_STATUS] || ''
    const reason = row.reason?.trim() || ''
    const naResponsibility = Responsibility.NOT_APPLICABLE
    const directResponsibility = Responsibility.DIRECT_RESPONSIBILITY
    const inheritedResponsibility = Responsibility.INHERITED
    const outsourcedResponsibility = Responsibility.OUTSOURCED_ATTESTATION
    const outsourcedNoAttestationResponsibility =
      Responsibility.OUTSOURCED_NO_ATTESTATION
    const naStatus = controlStatusTitle[CONTROL_STATUS.NOTAPPLICABLE]
    const vendor = row.vendor?.trim().toLowerCase() || ''
    const naVendor = NONE_NAME.toLowerCase()

    if (responsibility && responsibility !== directResponsibility && !reason) {
      return `Row ${rowNumber}: Responsibility is "${responsibility}". When Responsibility is not "Direct Responsibility", Reason must be provided.`
    }
    if (responsibility === naResponsibility && status !== naStatus) {
      return `Row ${rowNumber}: Responsibility is "${responsibility}", status is "${status}". When responsibility is "Not Applicable", status will be set to "Not Applicable". Double check your status value.`
    }
    if (status === naStatus && responsibility !== naResponsibility) {
      return `Row ${rowNumber}: Responsibility is "${responsibility}", status is "${status}". Responsibility must be "Not Applicable" when status is "Not Applicable". Double check your responsibility value.`
    }
    if (
      responsibility === outsourcedResponsibility ||
      responsibility === outsourcedNoAttestationResponsibility ||
      responsibility === inheritedResponsibility
    ) {
      if (!vendor || vendor === naVendor) {
        return `Row ${rowNumber}: Responsibility is ${responsibility}. When Responsibility is "Outsourced - Attestation", "Outsourced - No Attestation", or "Inherited", Vendor must be provided.`
      }
    }
    return null
  }

  const validatePolicyAndDepartment: RowValidator = (
    row: Record<string, string>,
    rowNumber: number,
  ): string | null => {
    const policySlots = getPolicySlots(row.policy || '')

    // The departments listed in the Department column, case-insensitive.
    // A policy can share its department with another policy, so we treat the
    // column as an unordered set rather than pairing it positionally.
    const listedDepartments = new Set(
      splitPipeValues(row.department || '', false).map((slot) =>
        slot.toLowerCase(),
      ),
    )
    const hasAnyDepartmentValue = [...listedDepartments].some(
      (slot) => slot !== NONE_ID,
    )

    if (policySlots.length === 0) {
      return hasAnyDepartmentValue
        ? `Row ${rowNumber}: Policy is required when department is provided.`
        : null
    }

    // Each listed policy's own department must appear in the Department column.
    for (const policyName of policySlots) {
      const policy = policyByKey.get(policyName.toLowerCase())
      // Invalid policies are reported by the Policy column validator;
      // a policy with no department has nothing to cross-check here.
      if (!policy || !policy.getDepartmentId()) continue
      const department = departmentById.get(policy.getDepartmentId())
      if (!department) continue
      const departmentIsListed =
        listedDepartments.has(department.getName().toLowerCase()) ||
        listedDepartments.has(department.getShortName().toLowerCase())
      if (!departmentIsListed) {
        return `Row ${rowNumber}: Policy "${policyName}" belongs to department "${department.getName()}", which is not listed in the Department column.`
      }
    }
    return null
  }

  const validateDuplicateControlId = CSV_COLUMN_VALIDATIONS.DUPLICATE_VALUES(
    'modelId',
    'Control ID',
  )

  return [
    validateResponsibility,
    validatePolicyAndDepartment,
    validateDuplicateControlId,
  ]
}

export const prepareControlsUpload = (
  complianceFrameworks: string[],
  policies: PolicyRecord[],
  rows: ImportControlCSVRow[],
): CreateUpdateControlRequest[] => {
  return rows.map((row) => {
    const control = new CreateUpdateControlRequest()
      .setModelId(new StringValue().setValue(row.modelId))
      .setName(new StringValue().setValue(row.name))
      .setObjective(new StringValue().setValue(row.objective))
    const policyModelIds = resolvePolicyModelIds(row.policy || '', policies)
    if (policyModelIds.length > 0) {
      control.setPolicyId(new StringValue().setValue(policyModelIds.join(',')))
    }
    if (row.procedure) {
      control.setDescription(new StringValue().setValue(row.procedure))
    }
    if (row.notes) {
      control.setNote(new StringValue().setValue(row.notes))
    }
    if (row.requiredEvidence) {
      control.setRequiredEvidence(
        new StringValue().setValue(row.requiredEvidence),
      )
    }
    if (row.testProcedures) {
      control.setTestProcedures(new StringValue().setValue(row.testProcedures))
    }
    if (row.status) {
      control.setStatus(
        new ControlStatusValue().setValue(
          row.status as unknown as CONTROL_STATUS,
        ),
      )
    }
    if (row.responsibility) {
      control.setResponsibility(
        new ResponsibilityValue().setValue(
          row.responsibility as unknown as RESPONSIBILITY,
        ),
      )
    }
    if (row.reason) {
      control.setNotApplicableReason(new StringValue().setValue(row.reason))
    }
    if (row.vendor) {
      control.setVendorId(new StringValue().setValue(row.vendor))
    }
    const owner = row.owner?.trim()
    if (owner && owner.toLowerCase() !== Owner.UNASSIGNED_BE) {
      control.setOwnerEmail(new StringValue().setValue(owner))
    }
    if (row.assignees) {
      const emails = row.assignees.split(',').filter(Boolean)
      if (emails.length > 0) {
        control.setAssigneeEmailsList(emails)
      }
    }
    if (row.risks) {
      const riskCustomIds = row.risks
        .split(',')
        .map((id) => id.trim())
        .filter(Boolean)
      if (riskCustomIds.length > 0) {
        control.setRiskCustomIds(new StringList().setItemList(riskCustomIds))
      }
    }

    control.setComplianceFrameworkIds(
      new StringList().setItemList(
        complianceFrameworks.filter((cFramework) => {
          return cFramework in row && row[cFramework] !== ''
        }),
      ),
    )

    control.setFrameworkIds(
      new StringList().setItemList(
        complianceFrameworks.flatMap((cFramework) => {
          // A "<no requirements>" cell maps the framework (handled via
          // compliance_framework_ids below) but contributes no requirements.
          if (
            cFramework in row &&
            row[cFramework] !== '' &&
            row[cFramework] !== NO_REQUIREMENTS_INDICATOR
          ) {
            const values = splitFrameworkCellForUpload(row[cFramework])
            return values.map((value) => {
              return `${cFramework}: ${value}`
            })
          } else {
            return []
          }
        }),
      ),
    )
    return control
  })
}

export const ERROR_MARKING_CONTROL_NA = 'Error marking control not applicable'
